/*
 * IV cart-drawer upsell zone — paging + add-to-cart.
 *
 * EVENT DELEGATION IS LOAD-BEARING, NOT A STYLE CHOICE.
 * The zone lives inside `.drawer__inner`, and both cart JS paths replace that node
 * wholesale on every mutation (CartDrawer.renderContents swaps #CartDrawer on add;
 * CartDrawerItems.getSectionsToRender swaps .drawer__inner on qty change / remove).
 * Any listener bound directly to a card, form or arrow is therefore dead the first
 * time the shopper touches the cart. Everything here delegates from `document` and
 * re-reads the DOM on each event, so it keeps working across unlimited re-renders.
 *
 * Progressive enhancement: each card is a real <form action="/cart/add" method="post">.
 * If this file fails to load, the button still submits and still adds the product —
 * the shopper just gets a full page navigation instead of an in-drawer update.
 */
(function () {
  'use strict';

  // Guard against this file being loaded twice. Every listener below is bound to
  // `document`, so a second execution would register a SECOND submit handler and
  // every "Add On" click would add the item twice. The PDP section deliberately
  // does not re-load this script for that reason, but the guard is cheap and this
  // is a silent, double-charging failure if it ever slips.
  if (window.__ivUpsellInit) return;
  window.__ivUpsellInit = true;

  var SEL = {
    zone: '[data-iv-upsell]',
    track: '[data-iv-upsell-track]',
    card: '[data-iv-upsell-card]',
    form: '[data-iv-upsell-form]',
    add: '[data-iv-upsell-add]',
    addText: '[data-iv-upsell-add-text]',
    error: '[data-iv-upsell-error]',
    prev: '[data-iv-upsell-prev]',
    next: '[data-iv-upsell-next]',
  };

  function routeAdd() {
    // Honour a localised/relativised cart route when the theme exposes one.
    if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url + '.js';
    return '/cart/add.js';
  }

  // ---------------------------------------------------------------- paging

  function cardStep(track) {
    var card = track.querySelector(SEL.card);
    if (!card) return track.clientWidth;
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function syncArrows(zone) {
    var track = zone.querySelector(SEL.track);
    if (!track) return;
    var prev = zone.querySelector(SEL.prev);
    var next = zone.querySelector(SEL.next);
    // 2px slack: fractional device-pixel widths make an exact comparison flicker
    // the "disabled" state at the ends of the track.
    var maxScroll = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
  }

  function page(zone, dir) {
    var track = zone.querySelector(SEL.track);
    if (!track) return;
    track.scrollBy({ left: dir * cardStep(track), behavior: 'smooth' });
  }

  // ------------------------------------------------------------ add to cart

  function setBusy(btn, busy, labelWhenBusy) {
    if (!btn) return;
    var text = btn.querySelector(SEL.addText);
    btn.disabled = busy;
    btn.classList.toggle('is-loading', busy);
    if (!text) return;
    if (busy) {
      text.textContent = labelWhenBusy || 'Adding…';
    } else {
      text.textContent = btn.getAttribute('data-iv-upsell-label-default') || 'Add On';
    }
  }

  function showError(card, message) {
    var box = card && card.querySelector(SEL.error);
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  }

  function submit(form) {
    var card = form.closest(SEL.card);
    var btn = form.querySelector(SEL.add);
    var drawer = document.querySelector('cart-drawer');

    showError(card, '');
    setBusy(btn, true);

    var body = {
      items: [
        {
          id: Number(new FormData(form).get('id')),
          quantity: 1,
        },
      ],
    };

    // Ask for the re-rendered drawer in the same round trip, so there is no second
    // fetch and no window where the drawer shows a stale cart.
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }

    fetch(routeAdd(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json().then(function (json) {
          // /cart/add.js reports a sold-out or invalid variant as HTTP 422 with a
          // `description`. Treating only network failure as error would show the
          // shopper a success state for an item that never entered the cart.
          if (!res.ok) {
            var err = new Error(json.description || json.message || 'Could not add that.');
            err.handled = true;
            throw err;
          }
          return json;
        });
      })
      .then(function (json) {
        // Clear the busy flag BEFORE the paint that consumes it. renderContents
        // replaces this subtree, so on the success path the button is discarded
        // either way — but if the zone ever stops being re-rendered, a flag cleared
        // after the paint leaves a permanently greyed, unclickable card.
        setBusy(btn, false);

        if (drawer && json.sections && typeof drawer.renderContents === 'function') {
          // STOCK DAWN BUG, reproduced and confirmed on this theme.
          // CartDrawer.renderContents() clears `is-empty` from `.drawer__inner` but
          // NOT from the <cart-drawer> element itself. open() then reads
          //   this.classList.contains('is-empty')
          // to decide what to trap focus on, sees a stale true, and reaches for
          // `.drawer__inner-empty` -- which renderContents has just replaced with the
          // populated cart. trapFocus() receives null and throws
          // "Cannot read properties of null (reading 'querySelectorAll')".
          //
          // Fires on the FIRST add into an empty cart, which is the single most common
          // add there is. The drawer still opens and the item is still in the cart, so
          // it looks harmless -- but focus never moves into the drawer, which strands
          // keyboard and screen-reader users behind it.
          //
          // Clearing the flag before the render restores the correct branch.
          drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        } else {
          // No drawer to refresh (or sections missing) — fall back to a reload so the
          // cart the shopper sees always matches the cart the server holds.
          window.location.reload();
          return;
        }

        document.dispatchEvent(
          new CustomEvent('iv:upsell:added', { detail: { items: json.items || null } })
        );
      })
      .catch(function (e) {
        setBusy(btn, false);
        showError(card, e && e.handled ? e.message : 'Could not add that. Try again.');
        if (!(e && e.handled)) console.error('[iv-upsell]', e);
      });
  }

  // ------------------------------------------------------------- delegation

  document.addEventListener('click', function (evt) {
    var prev = evt.target.closest && evt.target.closest(SEL.prev);
    if (prev) {
      evt.preventDefault();
      page(prev.closest(SEL.zone), -1);
      return;
    }
    var next = evt.target.closest && evt.target.closest(SEL.next);
    if (next) {
      evt.preventDefault();
      page(next.closest(SEL.zone), 1);
    }
  });

  document.addEventListener('submit', function (evt) {
    var form = evt.target.closest && evt.target.closest(SEL.form);
    if (!form) return;
    evt.preventDefault();
    submit(form);
  });

  // Scroll events do not bubble, so they cannot be delegated from document the way
  // click and submit are. Capture phase is the supported way to observe them globally,
  // and it survives the track being replaced because the listener is on document.
  document.addEventListener(
    'scroll',
    function (evt) {
      var track = evt.target;
      if (!track || !track.matches || !track.matches(SEL.track)) return;
      var zone = track.closest(SEL.zone);
      if (zone) syncArrows(zone);
    },
    true
  );

  var syncQueued = false;
  function syncAll() {
    // Coalesce bursts: a drawer re-render fires many mutations in one frame, and
    // syncArrows reads layout (scrollWidth/clientWidth). Running it per mutation
    // would force a reflow each time.
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(function () {
      syncQueued = false;
      document.querySelectorAll(SEL.zone).forEach(syncArrows);
    });
  }

  document.addEventListener('DOMContentLoaded', syncAll);
  // The zone is re-created on every cart mutation, so the arrow state has to be
  // recomputed then too — not just once at load.
  document.addEventListener('iv:upsell:added', syncAll);

  // Observe the <cart-drawer> element, NOT documentElement. The custom element itself
  // is stable across re-renders (only its #CartDrawer subtree is swapped), so this is
  // the narrowest node that still sees every change we care about. A document-wide
  // subtree observer would fire on every third-party app mutation on the page.
  function observeDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__ivUpsellObserved) return;
    drawer.__ivUpsellObserved = true;
    new MutationObserver(syncAll).observe(drawer, { childList: true, subtree: true });
    syncAll();
  }

  document.addEventListener('DOMContentLoaded', observeDrawer);
  if (document.readyState !== 'loading') {
    observeDrawer();
    syncAll();
  }
})();
